/**
 * Safari canvasFactory fix for Luminosity SMask / destination-in compositing bug.
 *
 * Root cause:
 *   pdfjs 5.x implements Luminosity SMask by:
 *     1. layerCtx.filter = addLuminosityFilter()   (feColorMatrix: A = 0.3R + 0.59G + 0.11B)
 *     2. layerCtx.globalCompositeOperation = "destination-in"
 *     3. layerCtx.drawImage(maskCanvas, sx, sy, sw, sh, dx, dy, dw, dh)
 *
 *   In Safari, destination-in within nested save()/restore() incorrectly handles
 *   the alpha channel — the gradient becomes 100% opaque instead of ~74%, covering
 *   the background photo entirely.
 *
 * Fix:
 *   Inject a custom CanvasFactory (pdfjs 5.x getDocument parameter) that patches each
 *   canvas context's drawImage. When destination-in is detected, pixel manipulation
 *   (getImageData / putImageData) replaces the native compositing:
 *   - Luminosity filter present → alpha = 0.3R + 0.59G + 0.11B (feColorMatrix formula)
 *   - No filter (alpha SMask)  → alpha = source alpha channel
 *   putImageData bypasses globalCompositeOperation, avoiding the Safari bug entirely.
 */

interface CanvasAndContext {
    canvas: HTMLCanvasElement | null;
    context: CanvasRenderingContext2D | null;
}

function applyDestinationIn(
    destCtx: CanvasRenderingContext2D,
    source: CanvasImageSource,
    rest: unknown[],
    filter: string,
): void {
    const { width, height } = destCtx.canvas;

    const destImageData = destCtx.getImageData(0, 0, width, height);
    const destData = destImageData.data;

    const tmp = document.createElement('canvas');
    tmp.width = width;
    tmp.height = height;
    const tmpCtx = tmp.getContext('2d');
    if (!tmpCtx) return;

    // Draw source at the exact position/size pdfjs specified
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (tmpCtx.drawImage as any)(source, ...rest);
    const srcData = tmpCtx.getImageData(0, 0, width, height).data;

    if (filter && filter !== 'none') {
        // Luminosity SMask: feColorMatrix "0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.3 0.59 0.11 0 0"
        // A_out = 0.3 * R_norm + 0.59 * G_norm + 0.11 * B_norm
        for (let i = 0; i < destData.length; i += 4) {
            const luminosity =
                (0.3 * srcData[i] + 0.59 * srcData[i + 1] + 0.11 * srcData[i + 2]) / 255;
            const maskAlpha = luminosity * (srcData[i + 3] / 255);
            destData[i + 3] = Math.round(destData[i + 3] * maskAlpha);
        }
    } else {
        // Alpha SMask: use source alpha directly
        for (let i = 0; i < destData.length; i += 4) {
            destData[i + 3] = Math.round(destData[i + 3] * (srcData[i + 3] / 255));
        }
    }

    // putImageData writes pixels directly, bypassing globalCompositeOperation
    destCtx.putImageData(destImageData, 0, 0);
}

function patchContextForSafari(ctx: CanvasRenderingContext2D): void {
    const origDrawImage = ctx.drawImage.bind(ctx);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ctx as any).drawImage = function (image: CanvasImageSource, ...rest: unknown[]) {
        if (ctx.globalCompositeOperation === 'destination-in') {
            applyDestinationIn(ctx, image, rest, ctx.filter);
            ctx.globalCompositeOperation = 'source-over';
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (origDrawImage as any)(image, ...rest);
    };
}

/**
 * pdfjs 5.x 通过 `new CanvasFactory({ ownerDocument, enableHWA })` 实例化，
 * 必须是 class 而非普通对象。
 *
 * 仅在 isIOSSafari 条件下使用，不影响其他浏览器。
 */
export class SafariGradientFixCanvasFactory {
    private _document: Document;

    constructor({
        ownerDocument = globalThis.document,
    }: { ownerDocument?: Document; enableHWA?: boolean } = {}) {
        this._document = ownerDocument;
    }

    create(width: number, height: number): CanvasAndContext {
        if (width <= 0 || height <= 0) throw new Error('Invalid canvas size');
        const canvas = this._document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        // willReadFrequently: true 与 pdfjs DOMCanvasFactory 保持一致：
        // iOS Safari GPU backing 下 putImageData 写 CPU 侧后 GPU 纹理不同步，
        // 导致后续 drawImage(scratchCanvas) 读到旧像素，修复失效。
        // CPU backing 可确保 getImageData/putImageData 即时生效。
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) throw new Error('Cannot get 2D context');
        patchContextForSafari(ctx);
        return { canvas, context: ctx };
    }

    reset(canvasAndContext: CanvasAndContext, width: number, height: number): void {
        if (!canvasAndContext.canvas) throw new Error('Canvas is not specified');
        if (width <= 0 || height <= 0) throw new Error('Invalid canvas size');
        canvasAndContext.canvas.width = width;
        canvasAndContext.canvas.height = height;
    }

    destroy(canvasAndContext: CanvasAndContext): void {
        if (!canvasAndContext.canvas) throw new Error('Canvas is not specified');
        canvasAndContext.canvas.width = 0;
        canvasAndContext.canvas.height = 0;
        canvasAndContext.canvas = null;
        canvasAndContext.context = null;
    }
}
